import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type {
  SiteContext,
  SiteSettings,
  ThemeMarketView,
  TranslationOverview,
} from '@icetus/contracts';
import {
  LOCALES,
  LOCALE_LABEL,
  SITE_BUSINESS_LIMIT,
  SITE_COMPANY_LIMIT,
  SITE_TITLE_LIMIT,
  normalizeQrUrl,
  normalizeWhatsapp,
  whatsappLink,
} from '@icetus/contracts';
import {
  ARCHETYPE_LABEL,
  brandSwatches,
  contrastRatio,
  ON_BRAND,
  suggestOnBrand,
  themeSummaries,
  type ThemeSummary,
} from '@icetus/theme-kit';
import { Badge, Button, Card } from '@icetus/ui';
import {
  fetchSiteSettings,
  fetchSites,
  fetchThemeCatalog,
  fetchThemeMarket,
  fetchTranslationOverview,
  saveSiteSettings,
} from '../api';
import { ImageUploader } from '../components/image-uploader';
import { BrandGenerator } from '../components/brand-generator';
import { DialPhoneField } from '../components/dial-phone-field';
import { LocaleFlag } from '../components/locale-flag';
import { MediaPickerButton } from '../components/media-picker';
import { ThemeThumb } from '../components/theme-thumb';
import { PICK_COLUMNS_MAX, pickColumns } from '../lib/pick-columns';
import { relativeLuminance } from '../lib/brand-logo';
import { normalizeThemeChoice } from '../lib/theme-choice';

/**
 * 站点信息（侧栏里的「站点信息」，路由 key 仍是 settings）。
 *
 * 这些字段不是"存起来给以后用的"：品牌色经 token 注入直接改变配色，Logo 进页头，
 * favicon 进浏览器标签页，联系方式进页脚，语言开关决定该语言版本存不存在
 * （关掉即 404、同时也从 sitemap 消失），主题与变体决定整套配色、字体与首屏排布。
 * 所以每项都标了它实际影响哪里，避免客户以为只是填个资料。
 */

type Locale = (typeof LOCALES)[number];

/**
 * 主题清单的**兜底值**（内置四套）。
 *
 * 在模块加载时算一次：`themeSummaries()` 内部要对每套主题做一次令牌解析
 * （几十个引用展开 + 对比度计算），放进组件体里就是每次输入都重算一遍。
 * 后台发布的主题不在这里 —— 那些由 `fetchThemeCatalog()` 补进来，
 * 否则"站点正在用一套发布主题"时，这一页会把它当成不存在的名字。
 */
const builtInThemes = themeSummaries();
const swatches = brandSwatches();

/** 与平台侧校验保持一致：只收 #rrggbb，服务端不认的值这里就别放行 */
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/**
 * 客户能不能自己改主色。
 *
 * 暂时关掉：绝大多数客户不会去动它，摆一排色卡 + 一个取色器 + 一段「会写进
 * 主题令牌」的解释，只会让人怀疑自己是不是漏配了什么。
 * 关掉只影响这一块的 UI：库里已有的 brandColor 照常参与渲染，
 * 主题的默认色也照旧。要恢复就把这里改成 true。
 */
const BRAND_COLOR_ENABLED = false;

/**
 * 稳定的"随机"：同一个站点每次进来都推出同样那两张推荐。
 *
 * 用 `Math.random()` 的问题不是"随机"本身，而是它每次渲染都换一个答案 ——
 * 客户在这页落下第一个字符（改站点名、敲色值），推荐卡就重排一遍，
 * 刚看中的那套跑到别处去了，连点都点不准。
 * 种子取站点 id：不同站点推不同的，但同一个站点永远一致。
 */
function seededOrder<T extends { name: string }>(items: T[], seed: string): T[] {
  const hash = (value: string): number => {
    let h = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
      h ^= value.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  };
  return items
    .map((item) => ({ item, key: hash(`${seed}|${item.name}`) }))
    .sort((a, b) => a.key - b.key)
    .map((entry) => entry.item);
}

export function SiteSettingsPage({
  tenantId,
  onGoMarket,
  onGoTranslation,
  onGoShell,
}: {
  tenantId: string;
  /** 付费主题未订阅时，卡片上的按钮要把客户送到主题市场，而不是干说一句"请先订阅" */
  onGoMarket?: () => void;
  /** 语言卡片上的「去翻译」。翻译是独立模块，设置页只负责把人送过去 */
  onGoTranslation?: () => void;
  /**
   * 跳「页头页脚」。
   *
   * 语言入口的排布控件 2026-11-03 挪去了那一页（页头的一部分就该在页头页里改），
   * 这一页只留一句指路 —— 语言清单（开了哪几门）仍归这里。
   */
  onGoShell?: () => void;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [draft, setDraft] = useState<SiteSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * 十六进制输入框的**本地草稿**。
   *
   * 不能直接受控于 `draft.branding.brandColor`：客户要从空开始敲 `#1f57d6`，
   * 敲到第二个字符时那个值还不是合法颜色，若每次输入都写回 draft，
   * 受控值会立刻弹回旧值 —— 表现为"这个框打不进字"。
   */
  const [hexInput, setHexInput] = useState('');
  /** 品牌标生成面板。只有 AI 一条路（浏览器排版那条已按客户要求下线） */
  const [brandGenOpen, setBrandGenOpen] = useState(false);
  /**
   * 主题权益（哪些付费主题此刻能用）。
   *
   * 单独拉一次市场接口而不是让 `/v1/sites/:id/settings` 带回来：
   * 那份响应是"这个站点的配置"，权益是"这个租户买过什么"，
   * 两件事的失效时机不一样（买完主题不该让站点设置缓存失效）。
   * 拉失败就当作"付费主题都不可用"——保存时服务端还会再拦一次，
   * 前端这层只是不让客户白点。
   */
  const [market, setMarket] = useState<ThemeMarketView | null>(null);
  /**
   * 正在看大图的那套主题（存名字不存对象）。
   *
   * 主题清单会随 `fetchThemeCatalog` 换一次数组，缓存对象会在刷新后
   * 指向一份过期数据；名字始终查得到最新的那一条。
   */
  const [previewTheme, setPreviewTheme] = useState<string | null>(null);
  /**
   * 大图里挑的首屏排版。
   *
   * 与 `draft.appearance.variant` 分开：客户在大图里点几个变体看看，
   * 取消退出时不该把页面表单弄脏。
   */
  const [previewVariant, setPreviewVariant] = useState<string>('');
  /** 大图里正在保存。「应用」要打一次接口，按钮必须当场变成不可点 */
  const [applyingTheme, setApplyingTheme] = useState(false);
  /**
   * 主题清单，先用内置四套铺上，接口回来换成全集（含后台发布的）。
   *
   * 站点当前主题若是发布主题，本地清单里查不到它：`activeTheme` 会回退到
   * 第一套，变体那一排也会变成空的 —— 客户看到的是"我明明用的是这套，
   * 页面上却没有它"。
   */
  const [themes, setThemes] = useState<ThemeSummary[]>(builtInThemes);
  /**
   * 各语言的翻译进度。
   *
   * 单独一个接口、单独一次请求，且**失败时静默**：设置页的主职责是改配置，
   * 翻译模块的仓储不可用时（比如没跑迁移）不该让整页报红 ——
   * 客户会以为"设置页坏了"，而实际上他要改的名字和颜色都还能改。
   */
  const [translation, setTranslation] = useState<TranslationOverview | null>(null);
  /**
   * 外观那一排的可用宽度（px）。
   *
   * 宽屏多露几套推荐、窄屏少露几张，列数由宽度反算（见 `pickColumns`）而不是写断点：
   * 同一套控制台在 1440 笔记本上内容宽 1128、27 寸上是 1608，断点猜不准的那一档
   * 不是右边空一大块，就是把卡片挤成一条。
   *
   * 用 `ResizeObserver` 而不是监听 window：侧栏折叠、分栏拖动时窗口尺寸没变，
   * window 那条事件一次都不会来。
   */
  const [picksWidth, setPicksWidth] = useState(0);
  const picksRo = useRef<ResizeObserver | null>(null);
  const picksRef = useCallback((el: HTMLDivElement | null) => {
    picksRo.current?.disconnect();
    picksRo.current = null;
    if (!el) return;
    setPicksWidth(el.clientWidth);
    picksRo.current = new ResizeObserver(([entry]) => {
      if (entry) setPicksWidth(entry.contentRect.width);
    });
    picksRo.current.observe(el);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSites(tenantId)
      .then((items) => {
        if (cancelled) return;
        setSites(items);
        setSiteId((prev) =>
          items.some((s) => s.site.id === prev) ? prev : (items[0]?.site.id ?? ''),
        );
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchThemeCatalog(tenantId)
      .then((res) => {
        if (!cancelled) setThemes(res.items);
      })
      .catch(() => {
        // 拿不到就用内置四套：付费主题本来也不在这个网格里，不影响主要功能
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchThemeMarket(tenantId)
      .then((next) => {
        if (!cancelled) setMarket(next);
      })
      .catch(() => {
        // 静默：权益拿不到时付费主题显示为"需订阅"，服务端仍会把关
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setNotice(null);
    fetchSiteSettings(tenantId, siteId)
      .then((s) => {
        if (cancelled) return;
        /**
         * 原样进表单，**不在这里归一**。
         *
         * 这里曾经调 `normalizeAppearance()`，而它只认识 theme-kit 内置的四套：
         * 站点在跑后台发布的主题（钢市）时，这个函数判定"未知主题"，回退成
         * aurora/classic —— 外观卡把"当前使用"打在极光上，而保存时这个值会被
         * 一起写库，客户改个电话就把整站主题换掉。
         *
         * 变体该修（换主题时会沿用上一套的变体名），但得等 `fetchThemeCatalog()`
         * 把真实清单拿回来才能判断 —— 见下面那个按清单归一的 effect。
         */
        setSettings(s);
        setDraft(s);
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  /**
   * 主题清单回来后，按**清单**修一次变体（只修变体，主题名一律以服务端为准）。
   *
   * 为什么不在 fetch 里做：那两个请求是并发的，设置常常先到，此刻 `themes`
   * 还是内置四套，拿它判等于重演上面那个 bug。所以放在 effect 里，等清单到位。
   *
   * `settings` 与 `draft` 一起改：只改 draft 会让表单一打开就是"有未保存修改"。
   */
  useEffect(() => {
    if (!draft) return;
    const next = normalizeThemeChoice(draft.appearance, themes);
    if (next === draft.appearance) return;
    setDraft((prev) => (prev ? { ...prev, appearance: next } : prev));
    setSettings((prev) => (prev ? { ...prev, appearance: next } : prev));
  }, [themes, draft]);

  /**
   * 翻译进度。
   *
   * 依赖 `siteId` 而不是 `settings`：客户在译文里改一个字
   * 不该让这里重新拉一遍全站抽取（那是几百段文本的处理）。
   * 但要依赖 `settings.locales`：刚开通/关闭语言之后进度条必须跟着变，
   * 否则出现了"我明明开了西语，卡片还说未开通"。
   */
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchTranslationOverview(tenantId, siteId)
      .then((next) => {
        if (!cancelled) setTranslation(next);
      })
      .catch(() => {
        // 静默：翻译模块不可用不该影响设置页的其它功能
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId, settings?.locales]);

  // 外部改色（点色卡、换站点、恢复主题默认）时把输入框同步过来
  useEffect(() => {
    setHexInput(draft?.branding.brandColor ?? '');
  }, [draft?.branding.brandColor]);

  const dirty = JSON.stringify(settings) !== JSON.stringify(draft);

  /**
   * 有未保存的修改时拦一下关窗/刷新。
   *
   * 客户传完 logo，看到"已上传"就切去看前台，那一次保存永远不会发生 ——
   * 线上实测过：13:07 传了两张图，服务端之后一条 PATCH 都没收到，
   * 于是前台当然什么都没变。
   */
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const patch = (change: Partial<SiteSettings>) => {
    setDraft((prev) => (prev ? { ...prev, ...change } : prev));
    setNotice(null);
  };

  const onSave = async () => {
    if (!draft) return;
    /**
     * 主题这一项**只有真的动过才提交**。
     *
     * 它不是一个"输入框改了才脏"的字段，而是从库里读出来再原样写回去的：
     * 中途任何一次归一/回退出错（历史上就出过，把钢市写成极光），
     * 客户只是保存个电话号码，整站外观就被换掉了。少发一个字段，
     * 这一整类事故就没有发生的余地。
     */
    const appearanceTouched =
      !settings ||
      draft.appearance.theme !== settings.appearance.theme ||
      draft.appearance.variant !== settings.appearance.variant;
    setBusy(true);
    try {
      const saved = await saveSiteSettings(tenantId, draft.siteId, {
        name: draft.name,
        title: draft.title,
        company: draft.company,
        business: draft.business,
        locales: draft.locales,
        localeDisplay: draft.localeDisplay,
        branding: draft.branding,
        contact: draft.contact,
        ...(appearanceTouched ? { appearance: draft.appearance } : {}),
      });
      setSettings(saved);
      setDraft(saved);
      setNotice('已保存。站点页面刷新后生效（浏览器可能缓存了几秒）。');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleLocale = (locale: Locale) => {
    if (!draft) return;
    // 默认语言不能取消：关掉它等于关掉 `/`，而客户几乎不会预期这个结果
    if (locale === draft.defaultLocale) return;
    const has = draft.locales.includes(locale);
    const next = has ? draft.locales.filter((l) => l !== locale) : [...draft.locales, locale];
    if (next.length === 0) return;
    patch({ locales: next });
  };

  /**
   * 两张轮换推荐的主题。
   *
   * `useMemo` 必须放在 `if (!draft)` **之前**：那是这个组件唯一的提前 return，
   * hook 落在它后面会让两次渲染的 hook 数量对不上（React 直接报错）。
   *
   * 种子是站点 id：同一个站点每次进来都是同样两张（客户刚看中的那套
   * 不会在他输入一个字之后换掉），不同站点看到的组合不一样。
   * 排除**已保存**的那套而不是 draft 里的：点一下推荐卡就会让 draft 变，
   * 若按 draft 算，推荐会在点选的瞬间重新洗牌。
   */
  const savedThemeName = settings?.appearance.theme ?? '';
  const recommended = useMemo(() => {
    const pool = themes.filter((t) => !t.paid && t.name !== savedThemeName);
    /**
     * 整池都排好序，取几张交给渲染那一刻决定。
     *
     * 宽屏要 5 张、窄屏只要 2 张，这里先按上限切一次省得白算；关键是**顺序稳定**
     * （种子是站点 id），所以窗口拉宽时只是往后多露几张，前面几张不会换位置。
     */
    return seededOrder(pool, siteId || tenantId).slice(0, PICK_COLUMNS_MAX + 1);
  }, [themes, savedThemeName, siteId, tenantId]);

  if (!draft) {
    return <p className="muted">{error ?? '加载中…'}</p>;
  }

  /** 当前主题的解析结果：默认主色、"页头效果"预览底色都从这里取 */
  const activeTheme = themes.find((t) => t.name === draft.appearance.theme) ?? themes[0]!;

  /**
   * 这一排：当前主题 + 若干轮换推荐 + 「更多主题」。
   *
   * 早先是套餐自带的几套全部平铺（主题一多就变成十几张），客户要在这堆里
   * 比"哪套像我"，可真正的答案往往就是"你正在用的这套"。于是收成"你在用什么 +
   * 还可以试试哪几套"，想多看的人走最后一格去市场 —— 那里才有大图和分类。
   *
   * 2026-11 起"哪几套"跟着宽度走：最后一格固定留给市场入口，剩下的格子分给推荐。
   * 宽屏（27 寸约 6 格）多露两套，笔记本（4 格）还是原来的两张。
   */
  const pickCount = Math.max(1, pickColumns(picksWidth) - 1);
  const picks = [activeTheme, ...recommended.filter((t) => t.name !== activeTheme.name)].slice(
    0,
    pickCount,
  );
  /** 实际渲染的卡片数（含末尾的市场入口）。网格按它定列数，不留空栏也不拉宽卡片 */
  const gridStyle = { '--pick-cols': picks.length + 1 } as CSSProperties;

  /** 市场那一条（有真实截图、描述、价格）。接口没回来时是 undefined，卡片退回配色示意 */
  const marketItemOf = (name: string) => market?.items.find((i) => i.name === name);

  /**
   * 打开大图。
   *
   * 预选变体分两种：点的是当前主题 → 带出表单里已经选好的那个（客户是来
   * 确认"我真的是这套吗"）；点的是别的主题 → 用该主题的默认款
   * （`compact` 在 aurora 和 meridian 里不是同一个东西，沿用旧 key 只会静默回退）。
   */
  const openThemePreview = (name: string) => {
    const summary = themes.find((t) => t.name === name);
    setPreviewTheme(name);
    setPreviewVariant(
      name === draft.appearance.theme ? draft.appearance.variant : (summary?.defaultVariant ?? ''),
    );
  };

  /**
   * 大图里点「应用」。
   *
   * 这里**直接保存**，不走"先选中、再回去点保存"：客户看到大图确认了，
   * 这个动作在主题市场里也是立刻生效的；让他关掉图再去找保存按钮，
   * 十次里有三次会以为已经生效（这一页的保存按钮在顶上，滚动到外观卡时早就看不见了）。
   *
   * 代价是这一页其它未保存的改动会一起提交 —— 但那份改动本来也只有一个出口
   * （「保存修改」），客户迟早要点；把半成品留在表单里反而更危险。
   * 保存失败时把选择留在表单上：改完别的问题再点保存，不用重新挑一遍。
   */
  const applyThemePreview = async () => {
    const summary = previewTheme ? themes.find((t) => t.name === previewTheme) : undefined;
    if (!summary) return;
    const variant =
      previewVariant && summary.variants.some((v) => v.key === previewVariant)
        ? previewVariant
        : summary.defaultVariant;
    const next: SiteSettings = {
      ...draft,
      appearance: { ...draft.appearance, theme: summary.name, variant },
    };
    setApplyingTheme(true);
    try {
      const saved = await saveSiteSettings(tenantId, next.siteId, {
        name: next.name,
        title: next.title,
        company: next.company,
        business: next.business,
        locales: next.locales,
        branding: next.branding,
        contact: next.contact,
        appearance: next.appearance,
      });
      setSettings(saved);
      setDraft(saved);
      setError(null);
      setNotice(`已应用「${summary.label.zh}」并保存，前台刷新就能看到。`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setDraft(next);
    } finally {
      setApplyingTheme(false);
      setPreviewTheme(null);
    }
  };

  /** 正在看大图的那套。用名字查最新的对象：主题清单刷新后旧对象是过期的 */
  const previewSummary = previewTheme
    ? (themes.find((t) => t.name === previewTheme) ?? null)
    : null;
  const previewInfo = previewTheme ? marketItemOf(previewTheme) : undefined;
  const previewHero = previewInfo?.previewHeroUrl ?? previewInfo?.previewCardUrl ?? null;

  /** 取色器与"留空"文案都要有确定的颜色：当前主题的默认主色 */
  const themeBrand = activeTheme.palette.brand;

  /**
   * Logo 那条页头示意图的配色，直接取当前主题的页头令牌。
   *
   * 用主题的真实配色而不是固定的浅灰：白底 logo 压深色页头会变成一块白方块，
   * 这件事只有放进页头配色里才看得出来 —— 在棋盘格上它只是"一张白图"。
   */
  const headerSampleStyle = {
    background: activeTheme.palette.headerBg,
    color: activeTheme.palette.headerText,
  };

  /**
   * 深色页头专用的标是"浅色内容 + 透明底"。
   *
   * 那种图摆在浅色棋盘格上几乎看不见 —— 客户会以为生成失败或者图是空的。
   * 棋盘格跟着页头一起变深，既不骗人（还能看出是透明底），也和实际效果一致。
   */
  const headerIsDark = relativeLuminance(activeTheme.palette.headerBg) < 0.28;

  /**
   * 客户填的这个色，按钮上的字到底能不能看清。
   *
   * 判定用渲染层**同一套**推导（`suggestOnBrand`），而不是这里另写一遍规则：
   * 两处不一致时，控制台说"没问题"、前台却是白字压浅黄，比不提示更糟。
   */
  const brandColor = HEX_COLOR.test(draft.branding.brandColor ?? '')
    ? (draft.branding.brandColor as string)
    : null;
  const brandHint = brandColor
    ? (() => {
        const onBrand = suggestOnBrand(brandColor);
        const ratio = Math.round((contrastRatio(onBrand, brandColor) ?? 0) * 10) / 10;
        const textColor = onBrand === ON_BRAND.dark ? '深色字' : '白字';
        return {
          level: ratio < 4.5 ? ('warn' as const) : ('ok' as const),
          text:
            ratio < 4.5
              ? `这个色太浅或太灰：按钮上用${textColor}只有 ${ratio}:1，客户可能看不清。建议调深一档。`
              : `按钮文字会自动用${textColor}（对比度 ${ratio}:1，达标）。`,
        };
      })()
    : null;

  /**
   * WhatsApp 那一条链接。
   *
   * 区号变成下拉框之后，"漏国家码"这类错不会再发生，但**位数不对**仍然可能
   * （多敲一位、少敲一位），而它会让整份设置在服务端被判 `invalid_settings` ——
   * 客户改的是站点名，报错却说设置无效。这条链接就是当场能看见的那个信号。
   */
  const waHref = whatsappLink(normalizeWhatsapp(draft.contact.whatsapp));
  /**
   * 位数够不够才算「输错了」。
   *
   * 这个计数不是摆设：号码框每敲一个字都会重算这条链接，只看 `!waHref` 的话，
   * 客户刚敲第一个字下面就红一行「号码位数不对」，红字从头闪到尾 ——
   * 而 8 位以下是"还没打完"，不是"打错了"。
   */
  const waDigits = (draft.contact.whatsapp ?? '').replace(/\D/g, '').length;

  return (
    <section className="stack">
      {/*
        站点选择与站点名合成一行。
        这两件事本来就是同一个对象：上面挑"改哪个站"，后面就是改它的名字。
        原来分成两行（选站行的下拉框 + 「基本信息」卡片里的「站点名称」输入框），
        中间还隔着一个卡片标题，读起来像两个不相干的设置。

        两个 label 之间留一段额外间距（.picker--name）：不加的话它们挨在一起，
        看起来像"站点 = 名称"的一个复合控件；隔开才读得出是两件事。
      */}
      <div className="tablebar">
        <label className="picker">
          <span>站点</span>
          <select className="field" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            {sites.map((s) => (
              <option key={s.site.id} value={s.site.id}>
                {s.site.name}
              </option>
            ))}
          </select>
        </label>
        <label className="picker picker--name">
          <span>站点名称</span>
          <input
            className="field field--site-name"
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
          />
        </label>
        {/*
          主营业务放在这一行，不放在「品牌」卡里。

          它是**站点级事实**（这家公司做什么），和站点名同属"这个站是谁"，
          而「品牌」卡只该管两张图 —— 之前把它塞在品牌卡顶上，客户要先读
          两行说明才能看到 Logo，那一页本来就没几个字，一段解释压过一张图。

          说明也不再挂在框下面：这一行是工具栏式布局，一段说明会把行高撑开，
          和旁边的下拉框不再同高。要点直接写进 placeholder —— 空着的时候
          它就是提示，填过之后本来也不需要再讲一遍。
        */}
        <label className="picker picker--business">
          <span>主营业务</span>
          <input
            className="field field--business"
            autoComplete="off"
            maxLength={SITE_BUSINESS_LIMIT}
            placeholder="一句话说清你做什么、卖什么"
            value={draft.business ?? ''}
            onChange={(e) => patch({ business: e.target.value || null })}
          />
        </label>
        <div className="tablebar__right">
          <Button variant="primary" onClick={onSave} disabled={busy || !dirty}>
            {dirty ? '保存修改' : '已保存'}
          </Button>
        </div>
      </div>

      {/*
        第二行：**访客看到的名字**，与上面那行"我在控制台怎么叫这个站"分开。

        站点名称（内部）与网站名称（对外）是两个字段，不要合并：
        内部名是控制台区分多个站用的（"北美站"、"独立站-DE"），
        对外名是页头那行字。客户开第二个站时两者必然不同 ——
        合成一个字段，要么页头出现只有他自己看得懂的内部叫法，
        要么站点下拉框里两个一模一样的选项、切换时认不出是哪个。

        两个都留空等于"跟租户名走"，所以不做必填：老站点一个都没填过，
        做成必填的话它们下次保存别的字段就会被拦住。
      */}
      <div className="tablebar tablebar--sub">
        <span className="tablebar__lead">对外展示</span>
        <label className="picker picker--title">
          <span>网站名称</span>
          <input
            className="field"
            autoComplete="off"
            maxLength={SITE_TITLE_LIMIT}
            placeholder="访客在页头看到的名字"
            value={draft.title ?? ''}
            onChange={(e) => patch({ title: e.target.value || null })}
          />
        </label>
        <label className="picker picker--company">
          <span>公司名称</span>
          <input
            className="field"
            autoComplete="off"
            maxLength={SITE_COMPANY_LIMIT}
            placeholder="页脚版权里的法定名称"
            value={draft.company ?? ''}
            onChange={(e) => patch({ company: e.target.value || null })}
          />
        </label>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice">{notice}</p> : null}

      <Card
        title="语言"
        description="每种语言是一套独立的页面与商品文案。开通之后，可以用「多语言翻译」从默认语言整站翻过去，不用重写一遍。"
        actions={
          <Button variant="ghost" size="sm" onClick={() => onGoTranslation?.()}>
            去翻译 →
          </Button>
        }
      >
        {/*
         * 说明放上面。
         *
         * 原来挂在四行清单的末尾，可它讲的是"关掉会怎样"——客户决定勾不勾之前
         * 就该看到，等滚到最底下才看见已经晚了。
         */}
        <p className="langnote muted">
          {'关掉的语言会立刻 404，并从 sitemap 与 hreflang 中移除；'}
          {'默认语言不能关闭（它等于首页）。改动保存后才生效。'}
        </p>

        <ul className="langlist">
          {LOCALES.map((l) => {
            const isDefault = l === draft.defaultLocale;
            const enabled = isDefault || draft.locales.includes(l);
            const status = translation?.locales.find((item) => item.locale === l) ?? null;
            /**
             * 这个语言的内容进度。
             *
             * 只有"已开通 + 不是默认语言 + 拿到了总览"才有得显示。
             * 拿不到总览（翻译模块的仓储不可用）时**什么都不显示**，
             * 而不是显示 0/0 —— 后者会让客户以为"翻译全丢了"。
             */
            const ratio = status && status.total > 0 ? status.done / status.total : 0;
            const done = status !== null && status.total > 0 && status.done >= status.total;
            return (
              <li key={l} className={`langcard${enabled ? ' is-on' : ''}`}>
                <div className="langcard__head">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={enabled}
                      disabled={isDefault}
                      onChange={() => toggleLocale(l)}
                    />
                    <LocaleFlag locale={l} />
                    <span className="langcard__name">{LOCALE_LABEL[l]}</span>
                  </label>
                  {isDefault ? <Badge tone="neutral">默认</Badge> : null}
                  {!isDefault && !enabled ? <span className="langcard__off">未开通</span> : null}
                  {!isDefault && enabled && status?.activeJob ? (
                    <Badge tone="warn">翻译中</Badge>
                  ) : null}
                  {!isDefault && enabled && status && !status.activeJob ? (
                    <Badge tone={done ? 'success' : status.done > 0 ? 'warn' : 'neutral'}>
                      {done ? '已翻完' : status.done > 0 ? '部分已翻' : '未翻译'}
                    </Badge>
                  ) : null}
                </div>

                {isDefault ? (
                  <p className="langcard__hint muted">
                    不占 URL 前缀（<code>/products</code>），其它语言各带 <code>/es</code>{' '}
                    这样的前缀。翻译都从这门往外走。
                  </p>
                ) : !enabled ? (
                  <p className="langcard__hint muted">
                    勾上保存后还要去「多语言翻译」翻过去，否则访客打开 <code>/{l}</code> 是 404。
                  </p>
                ) : status ? (
                  <>
                    <div className="meter meter--slim">
                      <div
                        className={`meter__fill meter__fill--${done ? 'success' : 'warn'}`}
                        style={{ inlineSize: `${Math.round(ratio * 100)}%` }}
                      />
                    </div>
                    <p className="langcard__hint">
                      {status.total === 0
                        ? '默认语言还没有可翻译的内容。'
                        : `${status.done} / ${status.total} 段已翻` +
                          (status.stale > 0 ? `，${status.stale} 段待重翻` : '') +
                          (status.draftPages > 0 ? `，${status.draftPages} 页未发布` : '')}
                    </p>
                  </>
                ) : (
                  <p className="langcard__hint muted">已开通。翻译进度去「多语言翻译」看。</p>
                )}

                {!isDefault && enabled ? (
                  <div className="langcard__ops">
                    <Button variant="subtle" size="sm" onClick={() => onGoTranslation?.()}>
                      {status && status.done > 0 ? '继续翻译' : '去翻译'}
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>

        {/*
          语言入口怎么摆（平铺 / 下拉 / 不显示）已经挪到「页头页脚 · 页头」了 ——
          那是页头的第二个元素（挨着 Logo、主导航），客户不会先在脑子里把它
          归到"站点设置"再去找。这里只留一句指路，因为这一页曾经有那个控件，
          会用的人第一反应还是回来看。
          没有 `onGoShell` 就不留空按钮：这一页也可能被别处嵌进去用。
        */}
        {draft.locales.length > 1 ? (
          <p className="langcard__hint muted">
            前台那个语言入口的排布（自动 / 平铺 / 下拉 / 不显示）在
            {onGoShell ? (
              <button type="button" className="linkbtn" onClick={onGoShell}>
                「页头页脚 · 页头」
              </button>
            ) : (
              '「页头页脚 · 页头」'
            )}
            里改。这里管的是<b>开了哪几门</b>。
          </p>
        ) : null}
      </Card>

      {/*
        品牌卡片只做一件事：让客户把两个图换掉。
        每块是「标题 + 推荐分辨率 + 棋盘格预览 + 场景示意 + 按钮 + 一行提示」。
        两种预览各有各的用处，不能互相替代：
          · 棋盘格 —— 文件本身长什么样，有没有多余白底、是不是糊的
          · 场景示意 —— 放进页头 / 浏览器标签页之后还剩多少，这是客户真正
            要判断的事（白 logo 压深色页头会变成一块白方块，只有放进去才看得见）
        其余解释全部去掉 —— 这一页要填的东西本来就不多，一段说明压过一张图，
        客户会先读字再去点按钮。
      */}
      <Card title="品牌">
        <div className="brand-assets">
          <div className="brand-asset">
            <div className="brand-asset__head">
              <span className="brand-asset__label">Logo</span>
              <span className="brand-asset__spec">推荐 480×160 · 3:1 横版</span>
            </div>
            <div
              className={`brand-asset__preview brand-asset__preview--logo${headerIsDark ? ' is-dark' : ''}`}
            >
              {draft.branding.logoUrl ? (
                <img src={draft.branding.logoUrl} alt="Logo 预览" />
              ) : (
                <span className="brand-asset__empty">未上传</span>
              )}
            </div>
            {/*
              棋盘格里只能看出"这张图长什么样"，看不出"放进页头好不好看"。
              白 logo 压在深色页头上会变成一块白方块 —— 这件事必须在页头配色里
              才暴露得出来。底色与文字色取当前主题的 headerBg / headerText，
              换主题时这条跟着变，客户不用自己脑补。
            */}
            <div className="brand-sample brand-sample--header" style={headerSampleStyle}>
              {draft.branding.logoUrl ? (
                <img src={draft.branding.logoUrl} alt="" />
              ) : (
                <span className="brand-sample__name">{draft.name || '站点名'}</span>
              )}
              <span className="brand-sample__nav">Products · About · Contact</span>
            </div>
            <p className="muted brand-sample__caption">前台页头里长这样（32px 高）</p>
            {/*
              没有 Logo 时先给一条 CTA，而不是让客户在三颗按钮里去分辨哪颗是"我没有图"。
              上传要求手上已经有一张图，而这一格最常见的现状恰恰是"什么都没有" ——
              生成入口必须比上传更早被看到，所以它单独占一行、用主色按钮。
              有 Logo 之后这条收起来，避免一直抢「更换」「移除」的位置。
            */}
            {draft.branding.logoUrl ? null : (
              <div className="brand-cta">
                <span className="brand-cta__text">
                  <strong>还没有 Logo？</strong>让 AI 按站点名画一个，十几秒出图，每张计入本月额度。
                </span>
                <Button onClick={() => setBrandGenOpen(true)} disabled={busy}>
                  生成 Logo
                </Button>
              </div>
            )}
            <div className="brand-asset__actions">
              {draft.branding.logoUrl ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBrandGenOpen(true)}
                  disabled={busy}
                >
                  自动生成
                </Button>
              ) : null}
              <ImageUploader
                tenantId={tenantId}
                siteId={draft.siteId}
                multiple={false}
                size="sm"
                label={draft.branding.logoUrl ? '更换 Logo' : '上传 Logo'}
                disabled={busy}
                onUploaded={(items) => {
                  const url = items[0]?.url;
                  if (!url) return;
                  patch({ branding: { ...draft.branding, logoUrl: url } });
                  setNotice('Logo 已上传，还要点一次「保存修改」才会生效。');
                }}
              />
              {/* 客户常把同一个 logo 传过两三次（不同底色版本），让他从库里挑
                  比"再传一遍"更接近他脑子里那张图 */}
              <MediaPickerButton
                tenantId={tenantId}
                siteId={draft.siteId}
                label="从媒体库选择"
                disabled={busy}
                onPick={(items) => {
                  const url = items[0]?.url;
                  if (!url) return;
                  patch({ branding: { ...draft.branding, logoUrl: url } });
                  setNotice('已选用媒体库里的 Logo，还要点一次「保存修改」才会生效。');
                }}
              />
              {draft.branding.logoUrl ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => patch({ branding: { ...draft.branding, logoUrl: null } })}
                >
                  移除
                </Button>
              ) : null}
            </div>
            <p className="muted brand-asset__hint">透明底 PNG；页头是深色主题时用浅色版本。</p>
            {/*
              Logo 放在自己的 CDN 上时才用得上，绝大多数客户用不到 ——
              折起来，别在主视觉里再开一个"上传口"。
              它改的是 logoUrl 这一个字段，所以必须待在 Logo 这张卡里：
              挂在两张卡外面，读起来像是"两个图都支持外链"。
            */}
            <details className="brand-link">
              <summary>Logo 已有外链？直接填地址</summary>
              <input
                className="field"
                placeholder="https://cdn.example.com/logo.png"
                aria-label="Logo 地址"
                value={draft.branding.logoUrl ?? ''}
                onChange={(e) =>
                  patch({ branding: { ...draft.branding, logoUrl: e.target.value || null } })
                }
              />
            </details>
          </div>

          <div className="brand-asset">
            <div className="brand-asset__head">
              <span className="brand-asset__label">浏览器图标</span>
              <span className="brand-asset__spec">推荐 256×256 · 1:1 正方形</span>
            </div>
            <div className="brand-asset__preview brand-asset__preview--icon">
              {draft.branding.faviconUrl ? (
                <img src={draft.branding.faviconUrl} alt="图标预览" />
              ) : (
                <span className="brand-asset__empty">未上传</span>
              )}
            </div>
            {/*
              图标离开棋盘格就只剩 16px，光看预览不知道"它到底出现在哪儿"。
              这条把图标放回它真正待的地方：浏览器标签页。
              未上传时插槽是虚线空位 —— 比写一句"这里是图标"直观。
            */}
            <div className="brand-sample brand-sample--tabbar">
              <div className="brand-sample__tab">
                {draft.branding.faviconUrl ? (
                  <img src={draft.branding.faviconUrl} alt="" />
                ) : (
                  <span className="brand-sample__slot" aria-hidden="true" />
                )}
                <span className="brand-sample__tabtitle">{draft.name || '站点名'}</span>
                <span className="brand-sample__tabclose" aria-hidden="true">
                  ×
                </span>
              </div>
              {/* 一个「＋」比任何文字都更快说明"这是标签栏"，也是唯一的浏览器特征 */}
              <span className="brand-sample__newtab" aria-hidden="true">
                ＋
              </span>
            </div>
            <p className="muted brand-sample__caption">浏览器标签页里长这样（16px）</p>
            <div className="brand-asset__actions">
              <ImageUploader
                tenantId={tenantId}
                siteId={draft.siteId}
                multiple={false}
                size="sm"
                label={draft.branding.faviconUrl ? '更换图标' : '上传图标'}
                disabled={busy}
                onUploaded={(items) => {
                  const url = items[0]?.url;
                  if (!url) return;
                  patch({ branding: { ...draft.branding, faviconUrl: url } });
                  setNotice('图标已上传，还要点一次「保存修改」才会生效。');
                }}
              />
              <MediaPickerButton
                tenantId={tenantId}
                siteId={draft.siteId}
                label="从媒体库选择"
                disabled={busy}
                onPick={(items) => {
                  const url = items[0]?.url;
                  if (!url) return;
                  patch({ branding: { ...draft.branding, faviconUrl: url } });
                  setNotice('已选用媒体库里的图标，还要点一次「保存修改」才会生效。');
                }}
              />
              {draft.branding.faviconUrl ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => patch({ branding: { ...draft.branding, faviconUrl: null } })}
                >
                  移除
                </Button>
              ) : null}
            </div>
            <p className="muted brand-asset__hint">图案越简单越好，别用白底图。</p>
          </div>
        </div>

        {/*
          主色选择暂时隐藏（BRAND_COLOR_ENABLED）。
          代码全部保留，把常量改成 true 就回来 —— 里面的 swatches / hexInput /
          brandHint 都还在被引用，关掉不会留下未使用变量。
        */}
        {BRAND_COLOR_ENABLED ? (
          <>
            <div className="form-row">
              <span>主色</span>
              <div className="swatches" role="group" aria-label="主色色卡">
                {swatches.map((s) => {
                  const active =
                    (draft.branding.brandColor ?? '').toLowerCase() === s.hex.toLowerCase();
                  return (
                    <button
                      key={s.hex}
                      type="button"
                      className={`swatch${active ? ' is-active' : ''}`}
                      style={{ background: s.hex, color: s.onBrand }}
                      aria-pressed={active}
                      title={`${s.label.zh} ${s.hex}`}
                      onClick={() => patch({ branding: { ...draft.branding, brandColor: s.hex } })}
                    >
                      {/* 色块上直接写字：客户不用理解"对比度"，一眼就看出这个底色能不能看清字 */}
                      <span className="swatch__sample">Aa</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-row">
              <span>自定义</span>
              <div className="color-row">
                <input
                  type="color"
                  className="color-input"
                  aria-label="取色器"
                  value={
                    HEX_COLOR.test(draft.branding.brandColor ?? '')
                      ? (draft.branding.brandColor as string)
                      : themeBrand
                  }
                  onChange={(e) =>
                    patch({ branding: { ...draft.branding, brandColor: e.target.value } })
                  }
                />
                <input
                  className="field field--hex"
                  /* 占位符直接用当前主题的默认主色：比写死一个示例色更有用，
                   而且不会引入一个与主题无关的裸色值 */
                  placeholder={themeBrand}
                  aria-label="主色十六进制值"
                  value={hexInput}
                  onChange={(e) => {
                    const next = e.target.value.trim();
                    setHexInput(next);
                    // 敲到合法值才写回：中间状态写回去会让输入框把自己的字吃掉
                    if (next === '') patch({ branding: { ...draft.branding, brandColor: null } });
                    else if (HEX_COLOR.test(next)) {
                      patch({ branding: { ...draft.branding, brandColor: next } });
                    }
                  }}
                />
                <Button
                  variant="ghost"
                  disabled={!draft.branding.brandColor}
                  onClick={() => patch({ branding: { ...draft.branding, brandColor: null } })}
                >
                  用主题默认色
                </Button>
              </div>
            </div>

            {hexInput !== '' && !HEX_COLOR.test(hexInput) ? (
              <p className="error">颜色要写成 6 位十六进制，形如 #1f57d6</p>
            ) : null}

            {/*
            对比度提示只在**真的看不清**时出现。
            改色这件事上，客户需要的是"这个色配白字不行"，不是一串 WCAG 解释；
            而绝大多数色是可以的，常驻提示只会让人学会无视它。
          */}
            {brandHint && brandHint.level === 'warn' ? (
              <p className="muted">
                <Badge tone="warn">对比度偏低</Badge> {brandHint.text}
              </p>
            ) : brandHint ? (
              <p className="muted">{brandHint.text}</p>
            ) : null}

            <p className="muted">
              主色会写进主题令牌：按钮、链接、强调色，以及按钮按下时的深一档颜色都用它。
              留空则用当前主题的默认色（{themeBrand}）。
            </p>
          </>
        ) : null}
      </Card>

      {/*
        生成面板放在品牌卡片之外、紧跟其后：它产出两个字段（logoUrl / faviconUrl），
        本来是两张卡里的东西，挂在任何一张卡里面都会让人以为"只改那一项"。
      */}
      <BrandGenerator
        open={brandGenOpen}
        onClose={() => setBrandGenOpen(false)}
        tenantId={tenantId}
        siteId={draft.siteId}
        siteName={draft.name}
        brandColor={draft.branding.brandColor}
        // 标最终压在页头上，配色得按这个底色挑 —— 深色页头选墨黑版就是一块黑
        headerBg={activeTheme.palette.headerBg}
        onApply={({ logoUrl, faviconUrl }) => {
          patch({
            branding: { ...draft.branding, logoUrl, faviconUrl },
          });
          setNotice('Logo 与浏览器图标已填入，还要点一次「保存修改」才会生效。');
        }}
      />

      <Card
        title="外观"
        description="点开大图看完整首屏，确认后应用。只换外观，页面内容、图片与文案一个字都不动。"
      >
        {/*
          一排卡：当前主题、N 张轮换推荐、市场入口（N 随宽度变，见 `pickColumns`）。
          每张卡的按钮都叫「应用主题」，点开的是**大图**而不是直接生效 ——
          和主题市场里同一个动作：先看完整首屏，在图上确认了再应用。
          没人会凭一张两百像素宽的缩略图换掉整站的样子。
        */}
        <div className="theme-grid theme-grid--picks" ref={picksRef} style={gridStyle}>
          {picks.map((t) => {
            const info = marketItemOf(t.name);
            const isCurrent = t.name === draft.appearance.theme;
            return (
              <article
                key={t.name}
                className={`theme-card theme-card--pick${isCurrent ? ' is-active' : ''}`}
              >
                <button
                  type="button"
                  className={`theme-card__preview${
                    info?.previewCardUrl ? '' : ' theme-card__preview--thumb'
                  }`}
                  title="看完整首屏"
                  onClick={() => openThemePreview(t.name)}
                >
                  {info?.previewCardUrl ? (
                    /* lazy：这一块可能有四张真图，不必都赶在首屏前下载 */
                    <img src={info.previewCardUrl} alt={`${t.label.zh} 首屏预览`} loading="lazy" />
                  ) : (
                    /* 还没上传截图的主题退回配色示意：它按真实令牌现画，比一块空白有用 */
                    <ThemeThumb theme={t} />
                  )}
                </button>
                <span className="theme-card__name">
                  {t.label.zh}
                  {isCurrent ? <Badge tone="success">当前使用</Badge> : null}
                </span>
                <span className="theme-card__desc">{t.description.zh}</span>
                <span className="theme-card__meta">
                  {ARCHETYPE_LABEL[t.archetype].zh} · {t.variants.length} 种变体 · v{t.version}
                </span>
                <div className="theme-card__actions">
                  {isCurrent ? (
                    /* 当前这套没有"应用"可点，但排版还能换 —— 原来这里是一颗灰的
                       「已在使用」，等于把换排版的路堵死了（只剩缩略图能点）。
                       卡片下面那排变体 chips 撤掉之后，这里就是唯一的入口。 */
                    <Button size="sm" variant="ghost" onClick={() => openThemePreview(t.name)}>
                      换排版
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => openThemePreview(t.name)}>
                      应用主题
                    </Button>
                  )}
                </div>
              </article>
            );
          })}

          {/*
            最后一格是去主题市场的入口。
            它刻意长得不一样（虚线、没有缩略图、居中的箭头）：长得一样就会被
            当成又一套可选主题，点下去却换了页面 —— 那是最容易让人以为点错的一种反馈。
            整张卡都可点：它没有第二个去处，不会像主题卡那样"点哪算哪个"。
          */}
          <button
            type="button"
            className="theme-card theme-card--link"
            onClick={() => onGoMarket?.()}
          >
            <span className="theme-card__link-icon" aria-hidden="true">
              →
            </span>
            <span className="theme-card__name">更多主题</span>
            <span className="theme-card__desc">
              主题市场里有按月的付费主题，风格差别比这里的变体大得多。
            </span>
            <span className="theme-card__meta">订阅后直接应用到站点</span>
            {/* 卡片本身就是按钮，这里只画一颗"看起来能点"的壳：真放一颗 <button>
                会形成按钮套按钮，浏览器与读屏都拿它没办法 */}
            <span className="theme-card__actions">
              <span className="ui-btn ui-btn--ghost ui-btn--sm">浏览主题市场</span>
            </span>
          </button>
        </div>
      </Card>

      {/*
        主题大图。
        和主题市场里那张图是同一件事：先看完整首屏，再决定要不要。
        区别只在于"应用"写到表单里（这一页是表单，见 applyThemePreview 的注释），
        市场页是直接落到站点。
      */}
      {previewSummary ? (
        <div
          className="theme-detail"
          role="dialog"
          aria-modal="true"
          aria-label={`${previewSummary.label.zh} 预览`}
          onClick={() => setPreviewTheme(null)}
        >
          <div className="theme-detail__panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="theme-detail__close"
              aria-label="关闭"
              title="关闭"
              onClick={() => setPreviewTheme(null)}
            >
              ×
            </button>
            {previewHero ? (
              <img
                className="theme-detail__hero"
                src={previewHero}
                alt={`${previewSummary.label.zh} 首屏预览`}
              />
            ) : (
              /* 没上传截图的主题：宁可给一张按真实令牌现画的示意，也不要一块空白 */
              <div className="theme-detail__nohero muted">
                这套主题还没上传预览图，下面是它的配色示意
                <ThemeThumb theme={previewSummary} />
              </div>
            )}
            <div className="theme-detail__body">
              <h3 className="theme-detail__title">
                {previewSummary.label.zh}
                <span className="muted"> {previewSummary.label.en}</span>
              </h3>
              <p className="theme-detail__desc">{previewSummary.description.zh}</p>
              <p className="muted">
                {previewSummary.paid ? '付费主题 · 已订阅' : '包含在套餐里'} ·{' '}
                {previewSummary.variants.length} 种变体
                {previewSummary.serifHeading ? ' · 衬线标题' : ''} · v{previewSummary.version}
              </p>

              {previewSummary.variants.length > 1 ? (
                <div className="form-row theme-detail__row">
                  <span>首屏排版</span>
                  <div className="chips">
                    {previewSummary.variants.map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        className={`pill${previewVariant === v.key ? ' pill--on' : ''}`}
                        aria-pressed={previewVariant === v.key}
                        onClick={() => setPreviewVariant(v.key)}
                      >
                        {v.label.zh}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/*
                已经在用这套、而且没换排版时按钮是禁用的：这个弹层唯一的作用
                就是"换"，没有可换的东西时给一颗能点的按钮只会让人多点一次。
              */}
              <div className="theme-detail__actions">
                <Button
                  disabled={
                    applyingTheme ||
                    (previewSummary.name === draft.appearance.theme &&
                      previewVariant === draft.appearance.variant)
                  }
                  onClick={() => void applyThemePreview()}
                >
                  {applyingTheme
                    ? '应用中…'
                    : previewSummary.name === draft.appearance.theme
                      ? '换用这套排版'
                      : '应用这套主题'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={applyingTheme}
                  onClick={() => setPreviewTheme(null)}
                >
                  取消
                </Button>
              </div>
              <p className="muted theme-detail__hint">
                应用后会立刻保存，前台刷新就能看到。只换外观，页面内容与文案一个字都不动。
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <Card title="联系方式">
        {/*
          这一组从"填了也没用"变成了真的会显示：渲染层此前根本没读
          `SiteSettings.contact`（`SiteContext` 里没有这一项），
          客户认真填了公司邮箱、电话，前台一个字都不出现，
          而体检报告还告诉他"没有可触达的渠道"。

          所以这里要写清楚**填了会出现在哪**，否则客户没法判断该不该填。
        */}
        <p className="muted settings-note">
          填哪几项就显示哪几项，出现在每个页面的页脚，同时用于搜索引擎里的公司信息。
        </p>
        {/* 这几个框写的是**公司对外**的联系方式，不是填表人自己的。挂 off 是为了
            别让浏览器拿"我的邮箱/我的地址"去自动填充 —— 这一页是边改边存的，
            填错一个字就直接进了客户的网站，而客户未必会发现（不像个人资料页那样
            还有个"未保存的改动"提示） */}
        <label className="field-block">
          <span>邮箱</span>
          <input
            className="field"
            autoComplete="off"
            placeholder="sales@yourcompany.com"
            value={draft.contact.email ?? ''}
            onChange={(e) =>
              patch({ contact: { ...draft.contact, email: e.target.value || null } })
            }
          />
        </label>
        <DialPhoneField
          label="电话"
          placeholder="574 8712 3456"
          value={draft.contact.phone}
          onChange={(v) => patch({ contact: { ...draft.contact, phone: v } })}
        />
        <label className="field-block">
          <span>地址</span>
          <input
            className="field"
            autoComplete="off"
            placeholder="Ningbo, Zhejiang, China"
            value={draft.contact.address ?? ''}
            onChange={(e) =>
              patch({ contact: { ...draft.contact, address: e.target.value || null } })
            }
          />
        </label>

        {/* 一条分隔线，不写小标题：上面是"公司信息"，下面是三个聊天工具，
            一眼看得出来是两组，再挂一行 12px 的灰字只是多一行要读的东西 */}
        <div className="settings-divider" aria-hidden="true" />

        <DialPhoneField
          label="WhatsApp 号码"
          mode="digits"
          placeholder="138 0013 8000"
          value={draft.contact.whatsapp}
          onChange={(v) => patch({ contact: { ...draft.contact, whatsapp: v } })}
        >
          {/* 号码的错法都很安静（位数不对、混了字母），唯一的信号就是这条链接。
              它只在实际填了号码时出现，空着的时候不占位置；位数还不够时
              按"没打完"处理，不提前报错 */}
          {waHref ? (
            <p className="muted">
              访客点开是 <code>{waHref}</code>
            </p>
          ) : waDigits >= 8 ? (
            <p className="error">号码位数不对：国际格式是 8–15 位。</p>
          ) : null}
        </DialPhoneField>

        <label className="field-block">
          <span>Telegram</span>
          <input
            className="field"
            autoComplete="off"
            placeholder="acme_sales 或 https://t.me/acme_sales"
            value={draft.contact.telegram ?? ''}
            onChange={(e) =>
              patch({ contact: { ...draft.contact, telegram: e.target.value || null } })
            }
          />
        </label>

        <div className="field-block">
          <span>微信二维码</span>
          {/*
            微信没有能点击的公开主页 —— `weixin://` 在浏览器里点不动，
            所以这一项存的是**图片**，访客扫码加。这也是它和其它几个
            结构不一样的原因：其它都是链接，只有它是图。
          */}
          <div className="brand-asset__actions">
            <ImageUploader
              tenantId={tenantId}
              siteId={draft.siteId}
              multiple={false}
              size="sm"
              label={draft.contact.wechatQrUrl ? '更换二维码' : '上传二维码'}
              disabled={busy}
              onUploaded={(items) => {
                const url = items[0]?.url;
                if (!url) return;
                patch({ contact: { ...draft.contact, wechatQrUrl: url } });
                setNotice('二维码已上传，还要点一次「保存修改」才会生效。');
              }}
            />
            <MediaPickerButton
              tenantId={tenantId}
              siteId={draft.siteId}
              label="从媒体库选择"
              disabled={busy}
              onPick={(items) => {
                const url = items[0]?.url;
                if (!url) return;
                patch({ contact: { ...draft.contact, wechatQrUrl: url } });
                setNotice('已选用媒体库里的二维码，还要点一次「保存修改」才会生效。');
              }}
            />
            {draft.contact.wechatQrUrl ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => patch({ contact: { ...draft.contact, wechatQrUrl: null } })}
              >
                移除
              </Button>
            ) : null}
          </div>
          {draft.contact.wechatQrUrl ? (
            <img className="settings-qr" src={draft.contact.wechatQrUrl} alt="微信二维码预览" />
          ) : null}
          {/*
            `normalizeQrUrl` 在这里再判一次：媒体库里放着的可能是
            `data:` 或畸形地址，前台会把它当图片地址渲染，先拦在表单上。
          */}
          {draft.contact.wechatQrUrl && !normalizeQrUrl(draft.contact.wechatQrUrl) ? (
            <p className="error">这个地址不能当图片用，换一张或重新上传。</p>
          ) : null}
        </div>
      </Card>

      {/*
       * 保存按钮浮在右下角。
       *
       * 顶上那个按钮在页面很长时会滚出视野：客户传完 logo 直接切去看前台，
       * 那一保存就没发生过 —— 这条是线上真踩出来的。
       */}
      {dirty ? (
        <div className="save-dock">
          <span className="save-dock__text">有未保存的修改</span>
          <Button variant="primary" onClick={onSave} disabled={busy}>
            {busy ? '保存中…' : '保存修改'}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
